/**
 * Chart region selection.
 * VectorCharts = US only. CHS web ENC = coastal/lake endpoints only.
 * Trent-Severn inland has no free official ENC tiles — use inland waterway mode.
 */

/** Trent-Severn Waterway corridor (Trenton → Georgian Bay). */
export function isTrentSevernWaterway(lat, lng) {
  return lat >= 43.95 && lat <= 45.2 && lng >= -80.2 && lng <= -77.2;
}

/**
 * Within TSW, CHS web ENC only renders at the Lake Ontario and Georgian Bay ends.
 * @returns {'lake_ontario_end' | 'georgian_bay_end' | 'inland' | null}
 */
export function getTswChartMode(lat, lng) {
  if (!isTrentSevernWaterway(lat, lng)) return null;
  if (lat <= 44.25 && lng >= -78.05) return 'lake_ontario_end';
  if (lat >= 44.72 && lng <= -79.45) return 'georgian_bay_end';
  return 'inland';
}

/** @returns {'canada' | 'canada_inland' | 'us' | 'other'} */
export function getMarineChartRegion(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'canada';

  const tswMode = getTswChartMode(lat, lng);
  if (tswMode === 'inland') return 'canada_inland';

  const inAlaska = lat >= 51 && lat <= 72 && lng >= -170 && lng <= -129;
  const inHawaii = lat >= 18 && lat <= 23 && lng >= -161 && lng <= -154;
  const inPuertoRico = lat >= 17 && lat <= 19 && lng >= -68 && lng <= -64;

  const inContinentalUS =
    lat >= 24 && lat <= 49.5 && lng >= -125 && lng <= -66;

  if (inAlaska || inHawaii || inPuertoRico || inContinentalUS) return 'us';

  if (lat >= 41 && lat <= 84 && lng >= -141 && lng <= -52) return 'canada';

  return 'other';
}

export function chartRegionLabel(region) {
  if (region === 'canada_inland') return 'Trent-Severn / inland';
  if (region === 'us') return 'US waters';
  if (region === 'canada') return 'Canadian waters';
  return 'international';
}

export function chartRegionDescription(
  region,
  { navionicsAvailable = false, cmapAvailable = false } = {}
) {
  if (navionicsAvailable) {
    return 'Navionics nautical charts — full coverage including Trent-Severn, with your vessel position on the chart.';
  }
  if (region === 'canada_inland') {
    if (cmapAvailable) {
      return 'C-MAP nautical charts — depth, channels, and aids for inland waterways (Trent-Severn, Kawarthas).';
    }
    return 'Inland waterway map + depth overlays. Configure C-MAP or Navionics on the server for full ENC depth on canals.';
  }
  if (region === 'canada') {
    return 'CHS official ENC + NONNA bathymetry — depth areas, buoys, and channels (Great Lakes & coastal Canada).';
  }
  if (region === 'us') {
    return 'US waters — NOAA or VectorCharts (if enabled).';
  }
  return 'Regional chart source selected automatically from your position.';
}
